import { useState } from 'react'
import { CreditCard } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { RecordPaymentModal } from '@/components/billing/record-payment-modal'
import { usePayments } from '@/hooks/use-payments'
import { formatKES, formatPaymentMethod } from '@/lib/utils'

function formatDate(unixSeconds?: number) {
  if (!unixSeconds) return '—'
  return new Date(unixSeconds * 1000).toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function MemberPaymentsTab({
  memberId,
  memberName,
}: {
  memberId: string
  memberName: string
}) {
  const [recordingPayment, setRecordingPayment] = useState(false)
  const { data, isLoading } = usePayments({ memberId })
  const payments = data?.data ?? []

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button size="sm" onClick={() => setRecordingPayment(true)}>
          <CreditCard size={14} className="mr-1.5" />
          Record payment
        </Button>
      </div>

      {isLoading ? (
        <p className="font-sans text-sm text-muted-foreground">Loading…</p>
      ) : payments.length === 0 ? (
        <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
          No payments recorded yet.
        </p>
      ) : (
        <div className="overflow-x-auto rounded border border-border-2">
          <table className="w-full font-sans text-sm">
            <thead>
              <tr className="border-b border-border-2 bg-cream-2 text-left text-label uppercase tracking-[0.08em] text-muted-foreground">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Invoice #</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Method</th>
                <th className="px-4 py-3">Reference</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-border-2 last:border-0">
                  <td className="px-4 py-3 text-ink">{formatDate(p.paidAt)}</td>
                  <td className="px-4 py-3 font-medium text-ink">{p.invoiceNumber}</td>
                  <td className="px-4 py-3 text-ink">{formatKES(p.amount)}</td>
                  <td className="px-4 py-3 text-ink">{formatPaymentMethod(p.method)}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {p.mpesaReceiptNumber ?? p.reference ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {recordingPayment && (
        <RecordPaymentModal
          memberId={memberId}
          memberName={memberName}
          onClose={() => setRecordingPayment(false)}
        />
      )}
    </div>
  )
}
